// LicensedStatesPicker — multi-select US state chip picker used by:
//   1. Signup (initial license capture)
//   2. Settings > Profile (self-serve edit for existing agents)
//
// Lifted out of Signup.jsx L280-373 verbatim in behavior/styling so the
// experience is identical in both places. Emits the array of 2-letter USPS
// codes through onChange. `value` is the controlled selection.
//
// Wiring: <LicensedStatesPicker value={arr} onChange={setArr} />

import React, { useState } from 'react';
import { ChevronDown, X, Check } from 'lucide-react';

export const ALL_STATES = [
  ['AL','Alabama'],['AK','Alaska'],['AZ','Arizona'],['AR','Arkansas'],['CA','California'],
  ['CO','Colorado'],['CT','Connecticut'],['DE','Delaware'],['DC','D.C.'],['FL','Florida'],
  ['GA','Georgia'],['HI','Hawaii'],['ID','Idaho'],['IL','Illinois'],['IN','Indiana'],
  ['IA','Iowa'],['KS','Kansas'],['KY','Kentucky'],['LA','Louisiana'],['ME','Maine'],
  ['MD','Maryland'],['MA','Massachusetts'],['MI','Michigan'],['MN','Minnesota'],
  ['MS','Mississippi'],['MO','Missouri'],['MT','Montana'],['NE','Nebraska'],['NV','Nevada'],
  ['NH','New Hampshire'],['NJ','New Jersey'],['NM','New Mexico'],['NY','New York'],
  ['NC','North Carolina'],['ND','North Dakota'],['OH','Ohio'],['OK','Oklahoma'],
  ['OR','Oregon'],['PA','Pennsylvania'],['RI','Rhode Island'],['SC','South Carolina'],
  ['SD','South Dakota'],['TN','Tennessee'],['TX','Texas'],['UT','Utah'],['VT','Vermont'],
  ['VA','Virginia'],['WA','Washington'],['WV','West Virginia'],['WI','Wisconsin'],['WY','Wyoming'],
];

export default function LicensedStatesPicker({ value = [], onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const selected = Array.isArray(value) ? value : [];

  function toggle(code) {
    if (selected.includes(code)) onChange(selected.filter((x) => x !== code));
    else onChange([...selected, code]);
  }

  return (
    <div className="relative">
      {/* Selected pills */}
      <div
        className="min-h-[44px] w-full rounded-lg bg-white/3 border border-white/10 focus-within:border-civix-purple transition cursor-pointer px-2 py-1.5 flex flex-wrap gap-1 items-center"
        onClick={() => setOpen((o) => !o)}
        data-testid="licensed-states-picker"
      >
        {selected.length === 0 && (
          <span className="text-sm text-white/30 px-1">
            {placeholder || 'Select states where you hold a license…'}
          </span>
        )}
        {selected.map((s) => (
          <span
            key={s}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-[#7C3AED]/30 border border-[#7C3AED]/50 text-white"
          >
            {s}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onChange(selected.filter((x) => x !== s)); }}
              className="hover:text-red-400 transition"
              aria-label={`Remove ${s}`}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        <ChevronDown className={`w-4 h-4 text-white/30 ml-auto shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </div>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border border-white/10 bg-[#0D0D1A] shadow-xl max-h-60 overflow-hidden">
          <div className="p-2 border-b border-white/10">
            <input
              type="text"
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search states…"
              className="w-full text-sm bg-white/5 border border-white/10 rounded-sm px-3 py-1.5 outline-hidden text-white placeholder-white/30"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <ul className="overflow-y-auto max-h-44">
            {ALL_STATES.filter(([code, name]) =>
              !search ||
              code.toLowerCase().includes(search.toLowerCase()) ||
              name.toLowerCase().includes(search.toLowerCase())
            ).map(([code, label]) => (
              <li
                key={code}
                className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-white/5 transition ${
                  selected.includes(code) ? 'text-civix-purpleSoft' : 'text-white/80'
                }`}
                onClick={(e) => { e.stopPropagation(); toggle(code); }}
              >
                <span className={`w-3 h-3 rounded-xs border flex items-center justify-center shrink-0 ${
                  selected.includes(code) ? 'bg-[#7C3AED] border-[#7C3AED]' : 'border-white/30'
                }`}>
                  {selected.includes(code) && <Check className="w-2.5 h-2.5 text-white" />}
                </span>
                <span className="font-medium w-8">{code}</span>
                <span className="text-white/50">{label}</span>
              </li>
            ))}
          </ul>
          <div className="border-t border-white/10 px-3 py-2 flex gap-2">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onChange(ALL_STATES.map(([c]) => c)); setOpen(false); }}
              className="text-xs text-white/50 hover:text-white transition"
            >
              Select all
            </button>
            <span className="text-white/20">·</span>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onChange([]); }}
              className="text-xs text-white/50 hover:text-white transition"
            >
              Clear
            </button>
            <span className="ml-auto text-xs text-white/30">{selected.length} selected</span>
          </div>
        </div>
      )}
    </div>
  );
}
